import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { EventClickArg } from "@fullcalendar/core"

import { useResourceBookingDraftActions } from "../ResourceBookingDraftContext"
import { enqueueSnackbar } from "notistack"

import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { fetchFreeBusyScheduleForServiceCenter, FreeBusyScheduleResponse } from "../../../createOrderApi"
import { StandardTimelineCalendar } from "../calendar/StandardTimelineCalendar"
import { generateCalendarEvents } from "../calendar/helpers"
import { ResourceCalendarGroupContent } from "./ResourceCalendarGroupContent"
import { ResourceCalendarLabelContent } from "./ResourceCalendarLabelContent"
import { ResourceBookingPreviewImage } from "./ResourceBookingPreviewImage"
import { usePreviewImageDialog } from "./usePreviewImageDialog"
import { createNewEvent, getBookingRangeFromAvailabilitySlot } from "./helpers"
import {
    ResourceBase,
    TimelineCalendarEvent,
    TimelineCalendarResource,
    TimelineResourceGroupLabelContent,
    TimelineResourceLabelContent,
} from "../calendar/fullCalendarTypes"
import { useResourceDetails } from "../context/ResourceDetailsProvider"

const INTERVAL_MINUTES = 15

const transformData = (data: FreeBusyScheduleResponse) => {
    return data?.resources || []
}

const WeeklyCalendarLoadingContext = createContext<ReadonlySet<string>>(new Set())

type WeeklyResourceLabelProps = {
    resource: TimelineResourceLabelContent["resource"]
    searchRegex: RegExp | undefined
    eventsLoadingNativeTitle: string
    handleOpenDetails: (id: string) => void
}

const WeeklyResourceLabel = memo(function WeeklyResourceLabel({
    resource,
    searchRegex,
    eventsLoadingNativeTitle,
    handleOpenDetails,
}: WeeklyResourceLabelProps) {
    const loadingUids = useContext(WeeklyCalendarLoadingContext)
    const isEventsLoading = loadingUids.has(resource.extendedProps.serviceCenterUid)
    return (
        <ResourceCalendarLabelContent
            resource={resource}
            searchRegex={searchRegex}
            handleOpenDetails={handleOpenDetails}
            isEventsLoading={isEventsLoading}
            eventsLoadingNativeTitle={eventsLoadingNativeTitle}
        />
    )
})

type WeeklyResourceBookingCalendarProps = {
    timelineResources: TimelineCalendarResource[]
    searchRegex: RegExp | undefined
    groupImagesMap: Map<string, string>
    handleBookClick: () => void
}

export const WeeklyResourceBookingCalendar = memo(function WeeklyResourceBookingCalendar({
    timelineResources,
    searchRegex,
    groupImagesMap,
    handleBookClick,
}: WeeklyResourceBookingCalendarProps) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const { selectedImage, openImageModal, closeImageModal } = usePreviewImageDialog()
    const { openResourceDetailsDialog } = useResourceDetails()

    const { setAndSyncDraftEvent } = useResourceBookingDraftActions()
    const { mutateAsync: callEventsMutation } = useApiMutation(fetchFreeBusyScheduleForServiceCenter, {})

    const eventsLoadingNativeTitle = l10n.getString("create-order-resource-row-events-loading-tooltip")

    const [loadingUids, setLoadingUids] = useState<ReadonlySet<string>>(
        () => new Set(timelineResources.map((r) => r.serviceCenterUid)),
    )

    const loadingUidsRef = useRef(loadingUids)
    loadingUidsRef.current = loadingUids

    useEffect(() => {
        setLoadingUids(new Set(timelineResources.map((r) => r.serviceCenterUid)))
    }, [timelineResources])

    const resourceUidToCalendarResourceId = useMemo(
        () => new Map(timelineResources.map((r) => [r.uid, r.id])),
        [timelineResources],
    )

    const createEventSourceFetcher = useCallback(
        (serviceCenterUid: string) =>
            async (
                fetchInfo: { startStr: string; endStr: string },
                successCallback: (events: TimelineCalendarEvent[]) => void,
                failureCallback: (error: any) => void,
            ) => {
                setLoadingUids((prev) => {
                    const next = new Set(prev)
                    next.add(serviceCenterUid)
                    return next
                })
                try {
                    const result = await callEventsMutation({
                        serviceCenterUid,
                        availabilityViewInterval: INTERVAL_MINUTES,
                        startTime: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromISO(fetchInfo.startStr)),
                        endTime: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromISO(fetchInfo.endStr)),
                    })

                    const slots = transformData(result).filter((slot) =>
                        resourceUidToCalendarResourceId.has(slot.resource.uid),
                    )
                    const events = generateCalendarEvents(
                        slots,
                        INTERVAL_MINUTES,
                        dateTimeFactory,
                        resourceUidToCalendarResourceId,
                    )

                    successCallback(events)
                } catch (err) {
                    failureCallback(err)
                } finally {
                    setLoadingUids((prev) => {
                        const next = new Set(prev)
                        next.delete(serviceCenterUid)
                        return next
                    })
                }
            },
        [callEventsMutation, dateTimeFactory, resourceUidToCalendarResourceId],
    )

    const eventSources = useMemo(() => {
        const uniqueServiceCenterUidList = [...new Set(timelineResources.map((r) => r.serviceCenterUid))]

        return uniqueServiceCenterUidList.map((uid) => ({
            id: `source-${uid}`,
            events: createEventSourceFetcher(uid),
        }))
    }, [timelineResources, createEventSourceFetcher])

    const handleEventClick = useCallback(
        (info: EventClickArg) => {
            const event = info.event
            const resourceId = event._def.resourceIds?.[0]

            if (!resourceId) return

            const resource = timelineResources.find((res) => res.id === resourceId)
            if (resource && loadingUidsRef.current.has(resource.serviceCenterUid)) {
                enqueueSnackbar({
                    variant: "info",
                    message: l10n.getString("create-order-resource-row-events-loading-tooltip"),
                    autoHideDuration: 2000,
                })
                return
            }

            const slotStart = dateTimeFactory.fromISO(event.startStr)
            const slotEnd = dateTimeFactory.fromISO(event.endStr)
            const now = dateTimeFactory.now()

            const bookingRange = getBookingRangeFromAvailabilitySlot({
                dateTimeFactory,
                slotStart,
                slotEnd,
                now,
                intervalMinutes: INTERVAL_MINUTES,
                allDayStartHour: 8,
                maxDurationHours: 1,
            })

            if (!bookingRange) {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-past-booking-attempt-message"),
                })
                return
            }

            const { effectiveStart, adjustedEnd, originalEnd } = bookingRange

            const start = dateTimeFactory.toApiISOMinutesFormat(effectiveStart)
            const end = dateTimeFactory.toApiISOMinutesFormat(adjustedEnd)
            const timeRange = { start, end: dateTimeFactory.toApiISOMinutesFormat(originalEnd) }

            const newEvent = createNewEvent(resourceId, start, end, timeRange)
            const fromTimeline = resource ? [{ ...resource, id: Number(resource.id) }] : []

            setAndSyncDraftEvent(newEvent, fromTimeline)
            handleBookClick()
        },
        [dateTimeFactory, l10n, timelineResources, setAndSyncDraftEvent, handleBookClick],
    )

    const resourceLabelContent = useCallback(
        ({ resource }: TimelineResourceLabelContent) => (
            <WeeklyResourceLabel
                resource={resource}
                searchRegex={searchRegex}
                eventsLoadingNativeTitle={eventsLoadingNativeTitle}
                handleOpenDetails={openResourceDetailsDialog}
            />
        ),
        [searchRegex, eventsLoadingNativeTitle, openResourceDetailsDialog],
    )

    const resourceGroupLabelContent = useCallback(
        ({ groupValue }: TimelineResourceGroupLabelContent) => (
            <ResourceCalendarGroupContent
                title={groupValue}
                groupImagesMap={groupImagesMap}
                onImageClick={openImageModal}
            />
        ),
        [groupImagesMap, openImageModal],
    )

    const isResourceLaneLoading = useCallback(
        (extendedProps: ResourceBase) => loadingUids.has(extendedProps.serviceCenterUid),
        [loadingUids],
    )

    const handleDatesSet = useCallback(() => {
        setLoadingUids(new Set(timelineResources.map((r) => r.serviceCenterUid)))
    }, [timelineResources])

    return (
        <WeeklyCalendarLoadingContext.Provider value={loadingUids}>
            <StandardTimelineCalendar
                viewMode="weekGrid"
                eventSources={eventSources}
                resources={timelineResources}
                resourceLabelContent={resourceLabelContent}
                resourceGroupLabelContent={resourceGroupLabelContent}
                handleEventClick={handleEventClick}
                isResourceLaneLoading={isResourceLaneLoading}
                onDatesSet={handleDatesSet}
            />
            {selectedImage && (
                <ResourceBookingPreviewImage previewImage={selectedImage} handleClose={closeImageModal} />
            )}
        </WeeklyCalendarLoadingContext.Provider>
    )
})
